/**
 * Utility functions for extracting and transforming global elements data
 * from the Optimizely CMS 'grg-web-uk-global' page
 */
import {
    fillGDIResponsiveInheritance,
    getGDIHideStyles,
    parseGDIViewTypes,
    resolveGDIStyles
} from '../components/optimizely/gdi/utils/gdi-styles'
import {typographyUnits, typographyStyle} from '../theme/foundations/typography'

const WEB_BREAKPOINTS = new Set(['base', 'md', 'lg'])

/**
 * Resolves a CMS FontSize token to an actual CSS value.
 * Handles direct typography units (e.g. 'webXl'), text style tokens (e.g. 'webH1'),
 * and direct CSS values (e.g. '30px').
 */
const resolveTypographyFontSize = (value) => {
    if (!value) return null
    if (typographyUnits[value]) return typographyUnits[value]
    const textStyle = typographyStyle[value]
    if (textStyle?.fontSize) {
        const base = typeof textStyle.fontSize === 'object'
            ? textStyle.fontSize.base
            : textStyle.fontSize
        return typographyUnits[base] || base
    }
    return value
}

/**
 * Extract header data from the global CMS content
 * @param {Object} globalContent - The global content from Optimizely CMS
 * @returns {Object} Extracted header data
 */
export const extractGlobalHeaderData = (globalContent) => {
    if (!globalContent?.item) {
        return null
    }

    const globalItem = globalContent.item

    // ── Logo ──
    // Raw GDIImageBlock at Logo[0].ContentLink.Expanded
    const logoImageBlock = globalItem.Logo?.[0]?.ContentLink?.Expanded || null

    // Resolved GDILinkBlock from the image block's link content area
    const logoLinkBlock =
        logoImageBlock?.GDIImageBlockLink?.[0]?.ContentLink?.Expanded ||
        logoImageBlock?.Link?.[0]?.ContentLink?.Expanded ||
        null

    const Logo = logoImageBlock
        ? {
              url:
                  logoImageBlock.Image?.Url ||
                  logoImageBlock.Image?.Expanded?.Url ||
                  null,
              altText: logoImageBlock.ImageAltText || 'Logo',
              maxWidth: logoImageBlock.MaxWidth || null,
              tabletUrl:
                  logoImageBlock.ImageTablet?.Url ||
                  logoImageBlock.ImageTablet?.Expanded?.Url ||
                  null,
              desktopUrl:
                  logoImageBlock.ImageDesktop?.Url ||
                  logoImageBlock.ImageDesktop?.Expanded?.Url ||
                  null,
              link: logoLinkBlock
                  ? {
                        linkType: logoLinkBlock.LinkType || null,
                        linkReference: logoLinkBlock.LinkReference || null,
                        ariaLabel: logoLinkBlock.AriaLabel || null,
                        openInNewTab: Boolean(logoLinkBlock.OpenInNewTab),
                        raw: logoLinkBlock
                    }
                  : null,
              imageBlock: logoImageBlock,
              raw: logoImageBlock
          }
        : null

    // ── Icons ──
    // Keyed by lower-cased IconType ('search', 'account', 'wishlist', 'cart');
    // every item carries its full raw entry alongside normalized fields. The
    // `linkBlock` field is the expanded GDILinkBlock for the icon's LinkOverride
    // (when authored), ready to be fed into resolveGDILink at the call site.
    const Icons = (globalItem.Icons || []).reduce((acc, iconItem) => {
        if (!iconItem?.IconType) return acc
        const key = iconItem.IconType.toLowerCase()
        const linkBlock =
            iconItem.LinkOverride?.[0]?.ContentLink?.Expanded || null
        acc[key] = {
            iconType: iconItem.IconType,
            iconUrl:
                iconItem.CustomIcon?.Url ||
                iconItem.CustomIcon?.Expanded?.Url ||
                null,
            ariaLabel: iconItem.AriaLabel || null,
            linkBlock,
            linkOverride: iconItem.LinkOverride || null,
            raw: iconItem
        }
        return acc
    }, {})

    // ── HeaderVariations ──
    // Ordered list; specific URL wins over wildcard (empty UrlToApply).
    const HeaderVariations = (globalItem.HeaderVariations || []).map((v) => ({
        urlToApply: v.UrlToApply || null,
        headerVariation: v.HeaderVariation || null,
        headerGradient: v.HeaderGradient || null,
        headerDropShadow: Boolean(v.HeaderDropShadow),
        raw: v
    }))

    // ── CategoryAddReplace ──
    // First-level main-navigation mutations keyed by view type. Each entry
    // declares a target CategoryId, an Action (Prepend / Append / Replace),
    // and a ReplaceBy content area of GDI blocks (typically GDICtaBlock).
    // The raw ContentArea is preserved so the consumer can splice it into the
    // existing first-level rendering via the shared GDI renderer.
    //
    // `showSubcategoriesCategoryId` (CMS field GDIGlobalComponentsPageShowSubcategories)
    // names the category (at any depth, not just L1) whose navigation content
    // (desktop popover / mobile drill-in) the inserted CTA should surface,
    // regardless of the CTA's own link target or the action's target CategoryId.
    const CategoryAddReplace = (globalItem.CategoryAddReplace || []).map((entry) => ({
        viewTypeToApply: entry.ViewTypeToApply || null,
        categoryId: entry.CategoryId || null,
        action: entry.Action || null,
        showSubcategoriesCategoryId: entry.GDIGlobalComponentsPageShowSubcategories || null,
        replaceBy: entry.ReplaceBy || [],
        raw: entry
    }))

    return {
        Logo,
        Icons,
        HeaderVariations,
        CategoryAddReplace
    }
}

/**
 * Extract search data from the global CMS content
 * @param {Object} globalContent - The global content from Optimizely CMS
 * @returns {Object} Extracted search data
 */
export const extractGlobalSearchData = (globalContent) => {
    if (!globalContent?.item) {
        return null
    }

    const globalItem = globalContent.item

    const stripHtmlTags = (html) => html?.replace(/<[^>]*>/g, '').trim() || null

    // Extract placeholder field: returns { text, style } or null
    const extractPlaceholderField = (fieldArray) => {
        const expanded = fieldArray?.[0]?.ContentLink?.Expanded
        if (!expanded) return null
        const text = stripHtmlTags(expanded.TextForWeb)
        if (!text) return null

        const styleEntries = expanded.GDITextBlockStylesPerViewType || []
        const result = {}

        const applyEntry = (entry, breakpoints) => {
            if (!breakpoints.length) return
            const set = (prop, val) => {
                if (!val) return
                if (!result[prop]) result[prop] = {}
                breakpoints.forEach((bp) => { result[prop][bp] = val })
            }

            set('color', entry.TextColor)
            set('fontStyle', entry.FontStyle)
            if (entry.FontSize) set('fontSize', resolveTypographyFontSize(entry.FontSize))
            set('textAlign', entry.TextAlignment)
        }

        for (const entry of styleEntries.filter((styleEntry) => !styleEntry?.ViewTypesToApply?.trim?.())) {
            applyEntry(entry, ['base', 'md', 'lg'])
        }

        for (const entry of styleEntries.filter((styleEntry) => styleEntry?.ViewTypesToApply?.trim?.())) {
            const breakpoints = parseGDIViewTypes(entry.ViewTypesToApply)
                .filter((bp) => WEB_BREAKPOINTS.has(bp))
            applyEntry(entry, breakpoints)
        }

        Object.keys(result).forEach((prop) => {
            result[prop] = fillGDIResponsiveInheritance(result[prop])
        })

        const viewTypesToApply = styleEntries
            .map((e) => e.ViewTypesToApply)
            .filter(Boolean)
            .join(',') || null

        return {text, style: {...result, viewTypesToApply}}
    }

    return {
        sfccFieldPlaceholder: extractPlaceholderField(globalItem.SFCCFieldPlaceholder),
        semanticFieldPlaceholder: extractPlaceholderField(globalItem.SemanticFieldPlaceholder)
    }
}

/**
 * Extract footer data from the global CMS content
 * @param {Object} globalContent - The global content from Optimizely CMS
 * @returns {Object} Extracted footer data
 */
export const extractGlobalFooterData = (globalContent) => {
    if (!globalContent?.item) {
        return null
    }

    const globalItem = globalContent.item

    // ListOfLinks – footer navigation columns
    const listOfLinks = (globalItem.ListOfLinks || []).map((column) => ({
        columnTitle: column.ColumnTitle || '',
        columnLinks: (column.ColumnLinks || [])
            .map((item) => item?.ContentLink?.Expanded)
            .filter(Boolean),
        columnTitleStyle: column.ColumnTitleStyle || [],
        columnLinksStyle: column.ColumnLinksStyle || []
    }))

    // NewsletterSubscription
    const ns = globalItem.GDIGlobalComponentsPageNewsletterSubscription || {}
    const nsTitleExp = (ns.NewsletterTitle || [])[0]?.ContentLink?.Expanded
    const nsPrivacyExp = (ns.PrivacyTextUnderField || [])[0]?.ContentLink?.Expanded
    const newsletterSubscription = {
        title: nsTitleExp
            ? {textForWeb: nsTitleExp.TextForWeb || '', stylesPerViewType: nsTitleExp.GDITextBlockStylesPerViewType || []}
            : null,
        privacyText: nsPrivacyExp
            ? {textForWeb: nsPrivacyExp.TextForWeb || '', stylesPerViewType: nsPrivacyExp.GDITextBlockStylesPerViewType || []}
            : null
    }

    // GoToApp
    const gta = globalItem.GoToApp || {}
    const gtaTitleExp = (gta.GDIGlobalComponentsPageTitle || [])[0]?.ContentLink?.Expanded
    const gtaSubtitleExp = (gta.GDIGlobalComponentsPageSubtitle || [])[0]?.ContentLink?.Expanded
    const goToApp = {
        title: gtaTitleExp
            ? {textForWeb: gtaTitleExp.TextForWeb || '', stylesPerViewType: gtaTitleExp.GDITextBlockStylesPerViewType || []}
            : null,
        subtitle: gtaSubtitleExp
            ? {textForWeb: gtaSubtitleExp.TextForWeb || '', stylesPerViewType: gtaSubtitleExp.GDITextBlockStylesPerViewType || []}
            : null,
        links: (gta.GDIGlobalComponentsPageLink || [])
            .map((item) => item?.ContentLink?.Expanded)
            .filter(Boolean),
        androidIconUrl: gta.AndroidIcon?.Url || '',
        iosIconUrl: gta.iOSIcon?.Url || '',
        qrCodeUrl: gta.QRCode?.Url || '',
        backgroundColor: gta.BackgroundColor || ''
    }

    // BottomBar
    const bb = globalItem.BottomBar || {}
    const bbTextLeftExp = (bb.BottomRowTextLeft || [])[0]?.ContentLink?.Expanded
    const bottomBar = {
        textLeft: bbTextLeftExp
            ? {textForWeb: bbTextLeftExp.TextForWeb || '', stylesPerViewType: bbTextLeftExp.GDITextBlockStylesPerViewType || []}
            : null
    }

    // SmsSubscription
    const sms = globalItem.SmsSubscription || {}
    const smsTitleExp = (sms.GDIGlobalComponentsPageTitle || [])[0]?.ContentLink?.Expanded
    const smsDescExp = (sms.GDIGlobalComponentsPageDescription || [])[0]?.ContentLink?.Expanded
    const smsPrivacyExp = (sms.PrivacyTextUnderField || [])[0]?.ContentLink?.Expanded
    const smsSubscription = {
        imageUrl: sms.Image?.Url || null,
        title: smsTitleExp
            ? {textForWeb: smsTitleExp.TextForWeb || '', stylesPerViewType: smsTitleExp.GDITextBlockStylesPerViewType || []}
            : null,
        description: smsDescExp
            ? {textForWeb: smsDescExp.TextForWeb || '', stylesPerViewType: smsDescExp.GDITextBlockStylesPerViewType || []}
            : null,
        privacyText: smsPrivacyExp
            ? {textForWeb: smsPrivacyExp.TextForWeb || '', stylesPerViewType: smsPrivacyExp.GDITextBlockStylesPerViewType || []}
            : null
    }

    return {listOfLinks, newsletterSubscription, smsSubscription, goToApp, bottomBar}
}

/**
 * Extract product badges data from the global CMS content
 * @param {Object} globalContent - The global content from Optimizely CMS
 * @returns {Array} Array of badge configurations
 */
export const extractProductBadgesData = (globalContent) => {
    if (!globalContent?.item) {
        return []
    }

    const globalItem = globalContent.item
    const badges = globalItem.Badges

    if (!badges?.length) {
        return []
    }

    return badges.map(badge => {
        if (!badge) return null

        return {
            id: badge.BadgeId || '',
            text: badge.BadgeText || '',
            textStyles: badge.GDIGlobalComponentsPageBadgeTextStyle || [],
            opacity: badge.GDIGlobalComponentsPageOpacity ?? 100,
            borderRadius: badge.GDIGlobalComponentsPageBorderRadius || 0,
            hideBlockOn: badge.HideBlockOn || '',
            raw: badge
        }
    }).filter(Boolean)
}


/**
 * Build Chakra-ready CMS override objects from a badge's textStyles, borderRadius, and opacity.
 *
 * Handles:
 * - Breakpoint inheritance via resolveGDIStyles (first entry = default for all BPs)
 * - FontSize design-system token routing to `textStyle` (e.g. 'badge' → textStyle)
 * - Opacity normalisation (0–100 percentage → 0–1 decimal)
 *
 * @param {Array}  textStyles   - GDIGlobalComponentsPageBadgeTextStyle array from CMS
 * @param {number} borderRadius - GDIGlobalComponentsPageBorderRadius value from CMS
 * @param {*}      opacity      - GDIGlobalComponentsPageOpacity value from CMS (0–1 or 0–100)
 * @param {string} hideBlockOn  - HideBlockOn string from CMS (e.g. "WebDesktop" or "WebMobile,WebTablet")
 * @returns {{ containerOverrides: Object, badgeOverrides: Object, hideStyles: Object }}
 */
export const resolveBadgeCmsStyles = (textStyles = [], borderRadius, opacity, hideBlockOn) => {
    const {styles: cms} = resolveGDIStyles(textStyles, {
        BackgroundColor: 'backgroundColor',
        TextColor:       'color',
        FontStyle:       'fontStyle',
        FontSize:        'fontSize',
        TextAlignment:   'textAlign',
        TextMaxWidth:    'maxWidth',
        Paddings:        'padding',
        Margins:         'margin'
    })

    const rawOpacity = opacity != null && opacity !== '' ? Number(opacity) : null
    const opacityValue = rawOpacity != null && !isNaN(rawOpacity)
        ? (rawOpacity > 1 ? rawOpacity / 100 : rawOpacity)
        : undefined

    const containerOverrides = {
        ...(cms.backgroundColor && {backgroundColor: cms.backgroundColor}),
        ...(cms.margin          && {margin: cms.margin}),
        ...(cms.maxWidth        && {maxWidth: cms.maxWidth}),
        ...(borderRadius        && {borderRadius: `${borderRadius}px`}),
        ...(opacityValue != null && {opacity: opacityValue})
    }

    const badgeOverrides = {
        ...(cms.backgroundColor && {backgroundColor: cms.backgroundColor}),
        ...(cms.color      && {color: cms.color}),
        ...(cms.fontStyle  && {fontStyle: cms.fontStyle}),
        ...(cms.fontSize   && {fontSize: cms.fontSize}),
        ...(cms.textStyle  && {textStyle: cms.textStyle}),
        ...(cms.textAlign  && {textAlign: cms.textAlign}),
        ...(cms.maxWidth   && {maxWidth: cms.maxWidth}),
        ...(cms.padding    && {padding: cms.padding}),
        ...(borderRadius   && {borderRadius: `${borderRadius}px`})
    }

    return {containerOverrides, badgeOverrides, hideStyles: getGDIHideStyles(hideBlockOn)}
}

/**
 * Extract top banner data from the global CMS content.
 * Maps TopBanner.TopBannerText[] (GDITextBlock), TopBannerVariation, and the
 * optional page-level link array.
 *
 * @param {Object} globalContent - The global content from Optimizely CMS
 * @returns {Object|null} Extracted top banner data or null when not present
 */
export const extractTopBannerData = (globalContent) => {
    if (!globalContent?.item) {
        return null
    }

    const topBanner = globalContent.item.TopBanner

    if (!topBanner) {
        return null
    }

    const texts = (topBanner.TopBannerText || [])
        .map((entry) => {
            const expanded = entry?.ContentLink?.Expanded
            if (!expanded) return null
            return {
                textForWeb: expanded.TextForWeb || '',
                textForApp: expanded.TextForApp || '',
                anchorId: expanded.TextAnchorId || '',
                stylesPerViewType: expanded.GDITextBlockStylesPerViewType || [],
                hideBlockOn: expanded.HideBlockOn || ''
            }
        })
        .filter(Boolean)

    const link = (topBanner.GDIGlobalComponentsPageLink || [])[0]?.ContentLink?.Expanded || null

    return {
        texts,
        variation: topBanner.TopBannerVariation || '',
        link
    }
}

/**
 * Extract the order tracking data from the global CMS content.
 * Maps the AboveFormContent CMS ContentArea, which is rendered above the
 * order tracking form. Raw ContentArea entries are preserved so the call site
 * can render each entry's Expanded block via renderLayoutsSwitch.
 *
 * @param {Object} globalContent - The global content from Optimizely CMS
 * @returns {Array|null} Extracted order tracking content entries or null when not present
 */
export const extractOrderTrackingData = (globalContent) => {
    if (!globalContent?.item) {
        return null
    }

    const orderTracking = (globalContent.item.AboveFormContent || []).filter(
        (entry) => entry?.ContentLink?.Expanded
    )

    if (orderTracking.length === 0) {
        return null
    }

    return orderTracking
}

export const extractNavigationData = (globalContent) => {
    if (!globalContent?.item) {
        return null
    }

    const globalItem = globalContent.item
    const headerRef = globalItem?.HeaderReference?.Expanded

    // ── CMS mega-menu structures (per-category) ──
    // MegaMenu: [{ CategoryId, ListOfLinksSource: 'FromSfcc'|'CustomLinks'|'IllustrationsOnly',
    //              CustomListOfLinks: [...columns], ContentTile: { Illustrations: [...] } }]
    // Per top-level SFCC category. Each entry carries its own columns + illustrations.
    const mapCustomListOfLinks = (cols) =>
        Array.isArray(cols)
            ? cols.map((col) => ({
                  columnTitle: col?.ColumnTitle || '',
                  columnLinks: (col?.ColumnLinks || []).filter(
                      (entry) => entry?.ContentLink?.Expanded
                  ),
                  listOfSubcategoriesFromSfcc: col?.ListOfSubcategoriesFromSfcc || null,
                  raw: col
              }))
            : []

    const MegaMenus = Array.isArray(globalItem.MegaMenu)
        ? globalItem.MegaMenu
              .filter((m) => m?.CategoryId)
              .map((m) => ({
                  categoryId: m.CategoryId,
                  listOfLinksSource: m.ListOfLinksSource || null,
                  customListOfLinks: mapCustomListOfLinks(m.CustomListOfLinks),
                  // Illustrations kept in CMS wrapper shape ({ContentLink:{Expanded}}) and
                  // filtered to entries with expanded content.
                  contentTileIllustrations: Array.isArray(m.ContentTile?.Illustrations)
                      ? m.ContentTile.Illustrations.filter(
                            (entry) => entry?.ContentLink?.Expanded
                        )
                      : [],
                  raw: m
              }))
        : []

    // Preserve legacy fields for the existing MegaMenuItemLevelOneBlock flow
    // attached through HeaderReference.
    return {
        CategoryReplacement: headerRef?.CategoryReplacement || null,
        ShopMenuContent: headerRef?.ShopMenuContent || null,
        MegaMenus,
        // ── MobileMenu ──
        // Configuration for the mobile/tablet drawer menu.
        //   - MenuType:
        //       * 'SFCC'   → backend-driven (default). The drawer fetches and
        //                    renders SFCC category data, augmented with
        //                    CategoryAddReplace CMS blocks.
        //       * 'Custom' → CMS-driven. The drawer skips ALL backend category
        //                    requests and renders GroupedCategories straight
        //                    from the CMS content area via the shared GDI
        //                    renderer.
        //   - GroupedCategories: raw CMS ContentArea used to populate the
        //     drawer body when MenuType is 'Custom'. Each entry is a
        //     ContentLink whose Expanded payload is a GDI layout/asset block
        //     (typically GDICarouselGridBlock); the custom-drawer-body renderer
        //     forwards each Expanded node to renderLayoutsSwitch.
        MobileMenu: {
            MenuType: typeof globalItem.MenuType === 'string' ? globalItem.MenuType : null,
            GroupedCategories: Array.isArray(globalItem.GroupedCategories)
                ? globalItem.GroupedCategories
                : []
        }
    }
}

/**
 * Extract LowResultsContent blocks from the global CMS content.
 * Rendered below the product grid when a search/filter returns ≤4 results,
 * or when filter manipulation returns 0 results.
 * @param {Object} globalContent - The global content from Optimizely CMS
 * @returns {Array|null} Array of expanded block objects, or null when not configured
 */
export const extractGlobalLowResultsContent = (globalContent) => {
    if (!globalContent?.item) return null
    const items = globalContent.item.LowResultsContent
    if (!Array.isArray(items) || items.length === 0) return null
    const blocks = items.map((item) => item?.ContentLink?.Expanded).filter(Boolean)
    return blocks.length > 0 ? blocks : null
}

export const extractDynamicOffersData = (globalContent) => {
    if (!globalContent?.item) return null
    const items = globalContent.item.DynamicOffers
    if (!Array.isArray(items) || items.length === 0) return null
    const blocks = items
        .map((item) => item?.ContentLink?.Expanded)
        .filter((expanded) => expanded && expanded.__typename)
    return blocks.length > 0 ? blocks : null
}

/**
 * Extract all global elements data from the CMS content
 * @param {Object} globalContent - The global content from Optimizely CMS
 * @returns {Object} All extracted global elements
 */
export const extractAllGlobalElements = (globalContent) => {
    return {
        header: extractGlobalHeaderData(globalContent),
        search: extractGlobalSearchData(globalContent),
        footer: extractGlobalFooterData(globalContent),
        badges: extractProductBadgesData(globalContent),
        navigation: extractNavigationData(globalContent),
        topBanner: extractTopBannerData(globalContent),
        orderTracking: extractOrderTrackingData(globalContent),
        lowResultsContent: extractGlobalLowResultsContent(globalContent),
        dynamicOffers: extractDynamicOffersData(globalContent),
    }
}
